/* eslint-disable jsx-a11y/anchor-has-content */
import { TrashIcon } from '@heroicons/react/24/outline'
import { ClipboardCheck, Notes, PercentageCircle, Prohibition, Trash } from 'iconoir-react'
import { Link, useParams } from 'react-router-dom'
import FloatingSave from 'app/components/FloatingSave'
import FormCombo from 'app/components/form/FormCombo'
import FormInput from 'app/components/form/FormInput'
import OfficeHoursEditor from 'app/components/form/OfficeHoursEditor'
import { SmartForm } from 'app/components/form/SmartForm'
import type { UseAIAgentLocationSettingsReturn } from './useAIAgentLocationSettings'

export type AIAgentLocationSettingsComponentProps = UseAIAgentLocationSettingsReturn & {
  validator?: any
}

export function AIAgentLocationSettingsComponent(props: AIAgentLocationSettingsComponentProps) {
  const { locationId } = useParams()
  const {
    agentConfig,
    setAgentConfig,
    initialAgentConfig,
    settingsV2Enabled,
    primaryTrackingNumber,
    setPrimaryTrackingNumber,
    businessPhone,
    setBusinessPhone,
    openHours,
    setOpenHours,
    isDirtyForm,
    setIsDirtyForm,
    unifiedServices,
    setUnifiedServices,
    newUnifiedServiceRowIds,
    newOfferRowIds,
    dirtyFields,
    markDirty,
    getDirtyClass,
    handleAddUnifiedService,
    handleDeleteUnifiedService,
    handleAddService,
    handleAddServiceNotOffered,
    handleLoadDefaults,
    handleAddOffer,
    handleDeleteService,
    handleDeleteServiceNotOffered,
    handleDeleteOffer,
    validator,
  } = props

  if (settingsV2Enabled) {
    return SettingsV2()
  }

  return SettingsLegacy()

  function SettingsV2() {
    // const voices = [
    //   { id: `3dzJXoCYueSQiptQ6euE`, name: `Heather` },
    //   { id: `CICpbs1ZGqlhQNbQmCUP`, name: `Lauren` },
    //   { id: `XcXEQzuLXRU9RcfWzEJt`, name: `Skylar` },
    //   { id: `L0yTtpRXzdyzQlzALhgD`, name: `Marissa` },
    //   { id: `gJx1vCzNCD1EQHT212Ls`, name: `Ava` },
    //   { id: `cNYrMw9glwJZXR8RwbuR`, name: `Emily` },
    //   { id: `OYTbf65OHHFELVut7v2H`, name: `Hope` },
    // ]
    // const defaultGreeting = `Thanks for calling {{businessName}}, this is {{botname}}. How can I help you today?`

    return (
      <SmartForm
        id="ai-agent-config-form"
        validator={validator}
        defaultValues={agentConfig}
        onChange={() => setIsDirtyForm(true)}
        className="!mb-32 !max-w-none !grid-cols-1 !gap-6 pb-40"
        hiddenInputs={[
          { name: `id`, value: agentConfig?.id },
          { name: `orgId`, value: agentConfig?.orgId },
          { name: `workspaceId`, value: agentConfig?.workspaceId },
          { name: `locationId`, value: agentConfig?.locationId },
        ]}
        hideButtons
      >
        {/* General Settings */}
        <div>
          <div className="flex items-center gap-2 rounded-b-none rounded-t-lg border border-default bg-surface-subtle px-4 py-3">
            <Notes height={20} />
            <p className="text-heading-xs text-strong">General Settings</p>
          </div>
          <div className="flex flex-col gap-6 rounded-lg !rounded-t-none border !border-t-0 border-default px-6 py-4">
            <div className={getDirtyClass(`agentName`)}>
              <FormInput
                name="agentName"
                label="Agent Name"
                required={true}
                labelClassName="text-label-sm text-strong"
                placeholder="Name of your AI Agent"
                cols={6}
                onChange={() => markDirty(`agentName`)}
              />
            </div>
            <div className={getDirtyClass(`businessName`)}>
              <FormInput
                name="businessName"
                label="Business Name"
                required={true}
                labelClassName="text-label-sm text-strong"
                placeholder="The name your AI Agent will call your practice"
                cols={6}
                onChange={() => markDirty(`businessName`)}
              />
            </div>
            <div className={getDirtyClass(`businessAddress`)}>
              <FormInput
                name="businessAddress"
                label="Business Address"
                required={true}
                labelClassName="text-label-sm text-strong"
                placeholder="The address of your business. Avoid using STE, AVE, etc."
                cols={6}
                onChange={() => markDirty(`businessAddress`)}
              />
            </div>
            <div className={getDirtyClass(`primaryTrackingNumber`)}>
              <FormInput
                name="primaryTrackingNumber"
                label="Redirect Phone"
                required={true}
                labelClassName="text-label-sm text-strong"
                placeholder="The number the agent will transfer to if needed"
                cols={6}
                value={primaryTrackingNumber}
                onChange={(e) => {
                  setPrimaryTrackingNumber(e.target.value)
                  markDirty(`primaryTrackingNumber`)
                }}
                infoTooltip={{
                  title: `Redirect Phone`,
                  message: `This maps to the location Primary Tracking Number`,
                }}
              />
              <p className="mt-1 text-body-xs text-minimal">This is the number that transfers go to</p>
            </div>
            <div className={getDirtyClass(`businessPhone`)}>
              <FormInput
                name="businessPhone"
                label="Office Phone Number"
                required={true}
                labelClassName="text-label-sm text-strong"
                placeholder="The number the Agent gives if a caller asks for the business number"
                cols={6}
                value={businessPhone}
                onChange={(e) => {
                  setBusinessPhone(e.target.value)
                  markDirty(`businessPhone`)
                }}
                infoTooltip={{
                  title: `Office Phone Number`,
                  message: `The number your AI agent will use when speaking.`,
                }}
              />
            </div>
            <Link className="text-body-sm text-blue-600 underline" to={`/settings/availability/${locationId}/settings`}>
              Manage insurance and double booking in Availability settings
            </Link>
            {/* <div className={getDirtyClass(`agentVoiceId`)}>
              <label className="text-label-sm text-trn-900">Agent Voice</label>
              <select
                name="agentVoiceId"
                className="mt-1 w-full rounded-md border border-trn-200 px-3 py-2 text-ui-sm"
                defaultValue={agentConfig?.agentVoiceId ?? ``}
                onChange={() => markDirty(`agentVoiceId`)}
              >
                <option value="">Select a voice</option>
                {voices.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                  </option>
                ))}
              </select>
            </div> */}
            {/* <div className={getDirtyClass(`greeting`)}>
              <label className="text-label-sm text-trn-900">Greeting</label>
              <textarea
                name="greeting"
                className="mt-1 w-full rounded-md border border-trn-200 px-3 py-2 text-ui-sm"
                rows={3}
                defaultValue={agentConfig?.greeting ?? defaultGreeting}
                placeholder={defaultGreeting}
                onChange={() => markDirty(`greeting`)}
              />
              <p className="mt-1 text-body-xs text-trn-400">
                {`Use {{businessName}} and {{botname}} as dynamic variables.`}
              </p>
            </div> */}
          </div>
        </div>

        {/* Offers Table */}
        <div>
          <div className="flex items-center justify-between rounded-b-none rounded-t-lg border border-default bg-surface-subtle px-4 py-3">
            <div className="flex items-center gap-2">
              <PercentageCircle height={20} />
              <p className="text-heading-xs text-strong">Offers</p>
            </div>
            <button
              type="button"
              data-testid="ai-agent-offers-add"
              className="btn btn-md border border-blue-600 bg-surface text-blue-600 hover:bg-blue-50"
              onClick={() => handleAddOffer()}
            >
              + Add Offer
            </button>
          </div>
          <div className="rounded-lg !rounded-t-none border !border-t-0 border-default">
            <div className="overflow-x-auto">
              <table data-testid="ai-agent-offers-table" className="w-full border-collapse text-ui-xs">
                <thead>
                  <tr className="border-b border-default bg-surface-subtle">
                    <th className="px-3 py-2 text-left font-semibold text-subtle">Offer Name</th>
                    <th className="px-3 py-2 text-left font-semibold text-subtle">Patient Status</th>
                    <th className="px-3 py-2 text-left font-semibold text-subtle">Insurance Status</th>
                    <th className="px-3 py-2 text-left font-semibold text-subtle">Emergency Only</th>
                    <th className="px-3 py-2 text-center font-semibold text-subtle">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {agentConfig?.offers?.map((offer, position) => (
                    <tr
                      key={position}
                      className={`border-b border-default ${
                        newOfferRowIds.has(position) ? `bg-[rgba(59,130,246,0.08)]` : ``
                      }`}
                    >
                      <td className="px-3 py-2">
                        <div className={getDirtyClass(`offers[${position}].name`)}>
                          <input
                            name={`offers[${position}].name`}
                            className="w-full rounded border border-default px-2 py-1 text-ui-xs"
                            placeholder="Add Offer Name"
                            value={offer.name}
                            onChange={(e) => {
                              setAgentConfig({
                                ...agentConfig,
                                offers: agentConfig?.offers?.map((o, i) =>
                                  i === position ? { ...o, name: e.target.value } : o,
                                ),
                              })
                              markDirty(`offers[${position}].name`)
                            }}
                          />
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <select
                          name={`offers[${position}].patientStatus`}
                          className="rounded border border-default px-2 py-1 text-ui-xs"
                          value={offer.patientStatus ?? `New Patient`}
                          onChange={(e) => {
                            setAgentConfig({
                              ...agentConfig,
                              offers: agentConfig?.offers?.map((o, i) =>
                                i === position
                                  ? { ...o, patientStatus: e.target.value as `New Patient` | `Existing Patient` }
                                  : o,
                              ),
                            })
                            markDirty(`offers[${position}].patientStatus`)
                          }}
                        >
                          <option value="New Patient">New Patient</option>
                          <option value="Existing Patient">Existing Patient</option>
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <select
                          name={`offers[${position}].eligibleInsurance`}
                          className="rounded border border-default px-2 py-1 text-ui-xs"
                          value={offer.eligibleInsurance ?? `Any`}
                          onChange={(e) => {
                            setAgentConfig({
                              ...agentConfig,
                              offers: agentConfig?.offers?.map((o, i) =>
                                i === position ? { ...o, eligibleInsurance: e.target.value } : o,
                              ),
                            })
                            markDirty(`offers[${position}].eligibleInsurance`)
                          }}
                        >
                          <option value="Any">Any</option>
                          <option value="Insured">Insured</option>
                          <option value="Uninsured">Uninsured</option>
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <select
                          name={`offers[${position}].onlyEmergencyEligible`}
                          className="rounded border border-default px-2 py-1 text-ui-xs"
                          value={offer.onlyEmergencyEligible ? `true` : `false`}
                          onChange={(e) => {
                            setAgentConfig({
                              ...agentConfig,
                              offers: agentConfig?.offers?.map((o, i) =>
                                i === position ? { ...o, onlyEmergencyEligible: e.target.value === `true` } : o,
                              ),
                            })
                            markDirty(`offers[${position}].onlyEmergencyEligible`)
                          }}
                        >
                          <option value="false">No</option>
                          <option value="true">Yes</option>
                        </select>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <button
                          type="button"
                          className="text-red-600 hover:text-red-800"
                          onClick={() => handleDeleteOffer(position)}
                        >
                          <Trash className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {(!agentConfig?.offers || agentConfig.offers.length === 0) && (
                    <tr>
                      <td colSpan={5} className="px-3 py-6 text-center text-minimal">
                        No offers configured. Click &quot;+ Add Offer&quot; to add one.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Unified Services Table */}
        <div>
          <div className="flex items-center justify-between rounded-b-none rounded-t-lg border border-default bg-surface-subtle px-4 py-3">
            <div className="flex items-center gap-2">
              <ClipboardCheck height={20} />
              <p className="text-heading-xs text-strong">Services</p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="ai-agent-services-load-defaults"
                className="btn btn-md border border-strong bg-surface text-default hover:bg-surface-subtle"
                onClick={() => handleLoadDefaults()}
              >
                Load Defaults
              </button>
              <button
                type="button"
                data-testid="ai-agent-unified-services-add"
                className="btn btn-md border border-blue-600 bg-surface text-blue-600 hover:bg-blue-50"
                onClick={() => handleAddUnifiedService()}
              >
                + Add Service
              </button>
            </div>
          </div>
          <div className="rounded-lg !rounded-t-none border !border-t-0 border-default">
            <div className="overflow-x-auto">
              <table data-testid="ai-agent-unified-services-table" className="w-full border-collapse text-ui-xs">
                <thead>
                  <tr className="border-b border-default bg-surface-subtle">
                    <th className="px-3 py-2 text-left font-semibold text-subtle">Service Name</th>
                    <th className="px-3 py-2 text-left font-semibold text-subtle">Offered?</th>
                    <th className="px-3 py-2 text-left font-semibold text-subtle">Low Price</th>
                    <th className="px-3 py-2 text-left font-semibold text-subtle">High Price</th>
                    <th className="px-3 py-2 text-left font-semibold text-subtle">Consult Cost</th>
                    <th className="px-3 py-2 text-center font-semibold text-subtle">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {unifiedServices.map((service, position) => (
                    <tr
                      key={position}
                      className={`border-b border-default ${
                        newUnifiedServiceRowIds.has(position) ? `bg-[rgba(59,130,246,0.08)]` : ``
                      }`}
                    >
                      <td className="px-3 py-2">
                        <input
                          type="hidden"
                          name={`unifiedServices[${position}].offered`}
                          value={service.offered ? `true` : `false`}
                        />
                        <div className={getDirtyClass(`unifiedServices[${position}].name`)}>
                          <input
                            name={`unifiedServices[${position}].name`}
                            className="w-full rounded border border-default px-2 py-1 text-ui-xs"
                            placeholder="Service name"
                            value={service.name}
                            onChange={(e) => {
                              setUnifiedServices(
                                unifiedServices.map((s, i) => (i === position ? { ...s, name: e.target.value } : s)),
                              )
                              markDirty(`unifiedServices[${position}].name`)
                            }}
                          />
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className={getDirtyClass(`unifiedServices[${position}].offered`)}>
                          <select
                            className="rounded border border-default px-2 py-1 text-ui-xs"
                            value={service.offered ? `true` : `false`}
                            onChange={(e) => {
                              const offered = e.target.value === `true`
                              setUnifiedServices(
                                unifiedServices.map((s, i) =>
                                  i === position
                                    ? offered
                                      ? { ...s, offered }
                                      : { ...s, offered, lowPrice: 0, highPrice: 0, consultPrice: 0 }
                                    : s,
                                ),
                              )
                              markDirty(`unifiedServices[${position}].offered`)
                            }}
                          >
                            <option value="true">True</option>
                            <option value="false">False</option>
                          </select>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        {service.offered ? (
                          <div className={getDirtyClass(`unifiedServices[${position}].lowPrice`)}>
                            <input
                              name={`unifiedServices[${position}].lowPrice`}
                              type="number"
                              className="w-24 rounded border border-default px-2 py-1 text-ui-xs"
                              value={service.lowPrice}
                              onChange={(e) => {
                                setUnifiedServices(
                                  unifiedServices.map((s, i) =>
                                    i === position ? { ...s, lowPrice: +e.target.value } : s,
                                  ),
                                )
                                markDirty(`unifiedServices[${position}].lowPrice`)
                              }}
                            />
                          </div>
                        ) : (
                          <>
                            <input type="hidden" name={`unifiedServices[${position}].lowPrice`} value="0" />
                            <span className="text-minimal">{`\u2014`}</span>
                          </>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {service.offered ? (
                          <div className={getDirtyClass(`unifiedServices[${position}].highPrice`)}>
                            <input
                              name={`unifiedServices[${position}].highPrice`}
                              type="number"
                              className="w-24 rounded border border-default px-2 py-1 text-ui-xs"
                              value={service.highPrice}
                              onChange={(e) => {
                                setUnifiedServices(
                                  unifiedServices.map((s, i) =>
                                    i === position ? { ...s, highPrice: +e.target.value } : s,
                                  ),
                                )
                                markDirty(`unifiedServices[${position}].highPrice`)
                              }}
                            />
                          </div>
                        ) : (
                          <>
                            <input type="hidden" name={`unifiedServices[${position}].highPrice`} value="0" />
                            <span className="text-minimal">{`\u2014`}</span>
                          </>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {service.offered ? (
                          <div className={getDirtyClass(`unifiedServices[${position}].consultPrice`)}>
                            <input
                              name={`unifiedServices[${position}].consultPrice`}
                              type="number"
                              className="w-24 rounded border border-default px-2 py-1 text-ui-xs"
                              value={service.consultPrice}
                              onChange={(e) => {
                                setUnifiedServices(
                                  unifiedServices.map((s, i) =>
                                    i === position ? { ...s, consultPrice: +e.target.value } : s,
                                  ),
                                )
                                markDirty(`unifiedServices[${position}].consultPrice`)
                              }}
                            />
                          </div>
                        ) : (
                          <>
                            <input type="hidden" name={`unifiedServices[${position}].consultPrice`} value="0" />
                            <span className="text-minimal">{`\u2014`}</span>
                          </>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <button
                          type="button"
                          className="text-red-600 hover:text-red-800"
                          onClick={() => handleDeleteUnifiedService(position)}
                        >
                          <Trash className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {unifiedServices.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-3 py-6 text-center text-minimal">
                        No services configured. Click &quot;+ Add Service&quot; or &quot;Load Defaults&quot; to get
                        started.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <FloatingSave isDirtyForm={isDirtyForm} />
      </SmartForm>
    )
  }

  function SettingsLegacy() {
    return (
      <SmartForm
        id="ai-agent-config-form"
        validator={validator}
        defaultValues={agentConfig}
        onChange={() => setIsDirtyForm(true)}
        className="!mb-32 !max-w-none !grid-cols-2 !gap-6"
        hiddenInputs={[
          { name: `id`, value: agentConfig?.id },
          { name: `orgId`, value: agentConfig?.orgId },
          { name: `workspaceId`, value: agentConfig?.workspaceId },
          { name: `locationId`, value: agentConfig?.locationId },
        ]}
        hideButtons
      >
        <div className="col-span-1 flex flex-col gap-6">
          <div>
            <div className="flex items-center gap-2 rounded-b-none rounded-t-lg border border-default bg-surface-subtle px-4 py-3">
              <Notes height={20} />
              <p className="text-heading-xs text-strong">General Settings</p>
            </div>
            <div className="flex flex-col gap-6 rounded-lg !rounded-t-none border !border-t-0 border-default px-6 py-4">
              <FormInput
                name="agentName"
                label="Agent Name"
                required={true}
                labelClassName="text-label-sm text-strong"
                placeholder="Name of your AI Agent"
                cols={6}
              />
              <FormInput
                name="businessName"
                label="Business Name"
                required={true}
                labelClassName="text-label-sm text-strong"
                placeholder="The name your AI Agent will call your practice"
                cols={6}
              />
              <FormInput
                name="businessAddress"
                label="Business Address"
                required={true}
                labelClassName="text-label-sm text-strong"
                placeholder="The address of your business. Avoid using STE, AVE, etc."
                cols={6}
              />
              <FormInput
                name="primaryTrackingNumber"
                label="Fallback Phone Number"
                required={true}
                labelClassName="text-label-sm text-strong"
                placeholder="The number the agent will transfer to if needed"
                cols={6}
                value={primaryTrackingNumber}
                onChange={(e) => {
                  setPrimaryTrackingNumber(e.target.value)
                  setIsDirtyForm(true)
                }}
                infoTooltip={{
                  title: `Fallback Phone Number`,
                  message: `The number your AI agent will transfer to when required.`,
                }}
              />
              <FormInput
                name="businessPhone"
                label="Office Phone Number"
                required={true}
                labelClassName="text-label-sm text-strong"
                placeholder="The number the Agent gives if a caller asks for the business number"
                cols={6}
                value={businessPhone}
                onChange={(e) => {
                  setBusinessPhone(e.target.value)
                  setIsDirtyForm(true)
                }}
                infoTooltip={{
                  title: `Office Phone Number`,
                  message: `The number your AI agent will use when speaking.`,
                }}
              />
              <Link
                className="text-body-sm text-blue-600 underline"
                to={`/settings/availability/${locationId}/settings`}
              >
                Manage insurance and double booking in Availability settings
              </Link>
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 rounded-b-none rounded-t-lg border border-default bg-surface-subtle px-4 py-3">
              <ClipboardCheck height={20} />
              <p className="text-heading-xs text-strong">Services Offered</p>
            </div>
            <div
              data-testid="ai-agent-services-offered-table"
              className="flex flex-col gap-6 rounded-lg !rounded-t-none border !border-t-0 border-default px-6 py-4"
            >
              {agentConfig?.services?.map((service, position) => (
                <div
                  key={position}
                  className="border-subtle grid grid-cols-2 gap-6 rounded-md border bg-surface-subtle p-4"
                >
                  <FormInput
                    name={`services[${position}].name`}
                    label="Service Name"
                    placeholder="What the AI calls this service"
                    cols={1}
                    value={service.name}
                    onChange={(e) =>
                      setAgentConfig({
                        ...agentConfig,
                        services: agentConfig?.services?.map((s, i) =>
                          i === position ? { ...s, name: e.target.value } : s,
                        ),
                      })
                    }
                  />
                  <div className="col-span-1 flex items-end gap-2.5">
                    <FormInput
                      name={`services[${position}].lowPrice`}
                      label="Price Range"
                      placeholder="Low Range"
                      cols={1}
                      value={service.lowPrice}
                      onChange={(e) =>
                        setAgentConfig({
                          ...agentConfig,
                          services: agentConfig?.services?.map((s, i) =>
                            i === position ? { ...s, lowPrice: +e.target.value } : s,
                          ),
                        })
                      }
                    />
                    <p className="mb-4 text-ui-sm text-default">to</p>
                    <FormInput
                      name={`services[${position}].highPrice`}
                      label=""
                      placeholder="High Range"
                      cols={1}
                      value={service.highPrice}
                      onChange={(e) =>
                        setAgentConfig({
                          ...agentConfig,
                          services: agentConfig?.services?.map((s, i) =>
                            i === position ? { ...s, highPrice: +e.target.value } : s,
                          ),
                        })
                      }
                    />
                  </div>
                  <FormCombo
                    name={`services[${position}].consultRequired`}
                    label="Consultation Required"
                    placeholder="Select an option"
                    options={[
                      { id: `true`, name: `Yes`, value: `true` },
                      { id: `false`, name: `No`, value: `false` },
                    ]}
                    value={
                      service.consultRequired
                        ? { id: `true`, name: `Yes`, value: `true` }
                        : { id: `false`, name: `No`, value: `false` }
                    }
                    className="w-20 bg-surface"
                    labelClassName="text-label-sm text-default"
                    onChange={(option) => {
                      setAgentConfig({
                        ...agentConfig,
                        services: agentConfig?.services?.map((s, i) =>
                          i === position ? { ...s, consultRequired: option.value === `true` } : s,
                        ),
                      })
                      setIsDirtyForm(true)
                    }}
                    cols={1}
                  />
                  <FormInput
                    name={`services[${position}].consultPrice`}
                    label="Consultation Cost"
                    placeholder="Flat Fee"
                    cols={1}
                    className="!w-48"
                    value={service.consultPrice}
                    disabled={!service.consultRequired}
                    onChange={(e) =>
                      setAgentConfig({
                        ...agentConfig,
                        services: agentConfig?.services?.map((s, i) =>
                          i === position ? { ...s, consultPrice: +e.target.value } : s,
                        ),
                      })
                    }
                  />
                  <button
                    className="btn btn-danger btn-md col-span-1 w-40"
                    type="button"
                    onClick={() => {
                      handleDeleteService(position)
                    }}
                  >
                    Delete Service
                  </button>
                </div>
              ))}
              <button
                type="button"
                data-testid="ai-agent-services-offered-add"
                className="btn btn-tertiary btn-md w-40"
                onClick={() => handleAddService()}
              >
                + Add Service
              </button>
            </div>
          </div>
        </div>
        <div className="col-span-1 flex flex-col gap-6">
          <div>
            <div className="flex items-center gap-2 rounded-b-none rounded-t-lg border border-default bg-surface-subtle px-4 py-3">
              <Prohibition height={20} />
              <p className="text-heading-xs text-strong">Services Not Offered</p>
            </div>
            <div
              data-testid="ai-agent-services-not-offered-table"
              className="flex flex-col gap-6 rounded-lg !rounded-t-none border !border-t-0 border-default px-6 py-4"
            >
              {agentConfig?.servicesNotOffered?.map((serviceNotOffered, position) => (
                <div key={position} className="grid w-[550px] grid-cols-6 items-center gap-6">
                  <input type="hidden" name={`servicesNotOffered[${position}].consultRequired`} value={`false`} />
                  <FormInput
                    name={`servicesNotOffered[${position}].name`}
                    cols={5}
                    value={serviceNotOffered.name}
                    onChange={(e) =>
                      setAgentConfig({
                        ...agentConfig,
                        servicesNotOffered: agentConfig?.servicesNotOffered?.map((s, i) =>
                          i === position ? { ...s, name: e.target.value } : s,
                        ),
                      })
                    }
                  />
                  <TrashIcon
                    className="col-span-1 h-6 w-6 cursor-pointer text-red-600"
                    onClick={async () => {
                      await handleDeleteServiceNotOffered(position)
                    }}
                  />
                </div>
              ))}
              <button
                type="button"
                data-testid="ai-agent-services-not-offered-add"
                className="btn btn-tertiary btn-md w-40"
                onClick={() => handleAddServiceNotOffered()}
              >
                + Add Service
              </button>
            </div>
          </div>
          <OfficeHoursEditor
            value={openHours}
            onChange={(hours) => {
              setOpenHours(hours)
              setIsDirtyForm(true)
            }}
            name="openHours"
            variant="panel"
          />
          <div>
            <div className="flex items-center gap-2 rounded-b-none rounded-t-lg border border-default bg-surface-subtle px-4 py-3">
              <PercentageCircle height={20} />
              <p className="text-heading-xs text-strong">Special Offers</p>
            </div>
            <div className="flex flex-col gap-6 rounded-lg !rounded-t-none border !border-t-0 border-default px-6 py-4">
              {agentConfig?.offers?.map((offer, position) => (
                <div
                  key={position}
                  className="border-subtle grid grid-cols-2 gap-6 rounded-md border bg-surface-subtle p-4"
                >
                  <FormInput
                    name={`offers[${position}].name`}
                    label="Name of Offer"
                    placeholder="What the AI calls this offer"
                    cols={1}
                    value={offer.name}
                    onChange={(e) =>
                      setAgentConfig({
                        ...agentConfig,
                        offers: agentConfig?.offers?.map((o, i) =>
                          i === position ? { ...o, name: e.target.value } : o,
                        ),
                      })
                    }
                  />
                  <FormCombo
                    name={`offers[${position}].patientStatus`}
                    label="Patient Status Eligibility"
                    placeholder="New / Existing"
                    options={[
                      { id: `New Patient`, name: `New`, value: `New Patient` },
                      { id: `Existing Patient`, name: `Existing`, value: `Existing Patient` },
                    ]}
                    value={
                      offer.patientStatus === `New Patient`
                        ? { id: `New Patient`, name: `New`, value: `New Patient` }
                        : { id: `Existing Patient`, name: `Existing`, value: `Existing Patient` }
                    }
                    className="w-36 bg-surface"
                    labelClassName="text-label-sm text-default"
                    onChange={(option) => {
                      setAgentConfig({
                        ...agentConfig,
                        offers: agentConfig?.offers?.map((o, i) =>
                          i === position ? { ...o, patientStatus: option.value } : o,
                        ),
                      })
                      setIsDirtyForm(true)
                    }}
                    cols={1}
                  />
                  <FormCombo
                    name={`offers[${position}].eligibleInsurance`}
                    label="Insurance Status Eligibility"
                    placeholder="Any / Insured / Uninsured"
                    options={[
                      { id: `Any`, name: `Any`, value: `Any` },
                      { id: `Insured`, name: `Insured`, value: `Insured` },
                      { id: `Uninsured`, name: `Uninsured`, value: `Uninsured` },
                    ]}
                    value={{
                      id: offer.eligibleInsurance ?? `Any`,
                      name: offer.eligibleInsurance ?? `Any`,
                      value: offer.eligibleInsurance ?? `Any`,
                    }}
                    className="w-36 bg-surface"
                    labelClassName="text-label-sm text-default"
                    onChange={(option) => {
                      setAgentConfig({
                        ...agentConfig,
                        offers: agentConfig?.offers?.map((o, i) =>
                          i === position ? { ...o, eligibleInsurance: option.value } : o,
                        ),
                      })
                      setIsDirtyForm(true)
                    }}
                    cols={1}
                  />
                  <FormCombo
                    name={`offers[${position}].onlyEmergencyEligible`}
                    label="Emergency Only?"
                    placeholder="Yes / No"
                    options={[
                      { id: `true`, name: `Yes`, value: `true` },
                      { id: `false`, name: `No`, value: `false` },
                    ]}
                    value={
                      offer.onlyEmergencyEligible
                        ? { id: `true`, name: `Yes`, value: `true` }
                        : { id: `false`, name: `No`, value: `false` }
                    }
                    className="w-20 bg-surface"
                    labelClassName="text-label-sm text-default"
                    onChange={(option) => {
                      setAgentConfig({
                        ...agentConfig,
                        offers: agentConfig?.offers?.map((o, i) =>
                          i === position ? { ...o, onlyEmergencyEligible: option.value === `true` } : o,
                        ),
                      })
                      setIsDirtyForm(true)
                    }}
                    cols={1}
                  />
                  <button
                    className="btn btn-danger btn-md col-span-1 w-40"
                    type="button"
                    onClick={() => {
                      handleDeleteOffer(position)
                    }}
                  >
                    Delete Offer
                  </button>
                </div>
              ))}
              <button
                type="button"
                data-testid="ai-agent-offers-add"
                className="btn btn-tertiary btn-md w-40"
                onClick={() => handleAddOffer()}
              >
                + Add Offer
              </button>
            </div>
          </div>
        </div>
        <FloatingSave isDirtyForm={isDirtyForm} />
      </SmartForm>
    )
  }
}
